import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notificationSettingsApi } from "@/api/notificationSettings";
import { Bell, Check, Loader2, Mail, Save, X, FileText } from "lucide-react";
import { toast } from "sonner";

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
    return (
        <button
            role="switch"
            aria-checked={checked}
            onClick={onChange}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                checked ? "bg-general-blue" : "bg-surface-container-high"
            }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    checked ? "translate-x-6" : "translate-x-1"
                }`}
            />
        </button>
    );
}

export function NotificationSettings() {
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery(notificationSettingsApi.getQueryOptions());

    const [followedPlayerEnabled, setFollowedPlayerEnabled] = useState(false);
    const [summaryEnabled, setSummaryEnabled] = useState(false);
    const [email, setEmail] = useState("");

    useEffect(() => {
        if (data) {
            setFollowedPlayerEnabled(data.followedPlayerMatchEmailEnabled);
            setSummaryEnabled(data.matchSummaryEmailEnabled);
            setEmail(data.notificationEmail ?? "");
        }
    }, [data]);

    const anyEnabled = followedPlayerEnabled || summaryEnabled;

    const isDirty =
        data !== undefined &&
        (followedPlayerEnabled !== data.followedPlayerMatchEmailEnabled ||
            summaryEnabled !== data.matchSummaryEmailEnabled ||
            email !== (data.notificationEmail ?? ""));

    const isValidEmail = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const hasValidationError = anyEnabled && !email;

    const saveMutation = useMutation({
        mutationFn: () =>
            notificationSettingsApi.updateSettings({
                followedPlayerMatchEmailEnabled: followedPlayerEnabled,
                matchSummaryEmailEnabled: summaryEnabled,
                notificationEmail: email || null,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["notification-settings"] });
            toast.success("Notification settings saved!", {
                icon: <Check className="w-4 h-4 text-general-green" />,
            });
        },
        onError: (error: Error) => {
            toast.error(error.message || "Failed to save settings", {
                icon: <X className="w-4 h-4 text-general-red" />,
            });
        },
    });

    return (
        <>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
                <div>
                    <h1 className="text-4xl font-black text-primary tracking-tight mb-2 font-headline">
                        Notification Settings
                    </h1>
                    <p className="text-on-surface-variant font-medium">
                        Choose which email notifications you want to receive.
                    </p>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => saveMutation.mutate()}
                        disabled={
                            isLoading ||
                            !isDirty ||
                            hasValidationError ||
                            !isValidEmail ||
                            saveMutation.isPending
                        }
                        className={`cursor-pointer px-4 py-2 rounded-md font-headline font-bold uppercase tracking-wider text-xs transition-all flex items-center gap-2 ${
                            isDirty && !hasValidationError && isValidEmail && !saveMutation.isPending
                                ? "bg-general-blue text-white hover:opacity-90"
                                : "bg-surface-container-high text-on-surface-variant cursor-not-allowed"
                        }`}
                    >
                        {saveMutation.isPending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                Save Changes
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="flex-1 min-h-0 p-4 bg-surface-container-low rounded-xl flex flex-col">
                <div className="flex-1 overflow-auto">
                    <div className="space-y-4 p-2">
                        <div className="card rounded-md">
                            <div className="flex items-center gap-3 p-4 border-outline-variant/20">
                                <div className="p-2 rounded-md text-white flex-shrink-0 bg-blue-500">
                                    <Bell className="w-5 h-5" />
                                </div>
                                <h2 className="text-lg font-headline font-bold text-on-surface">
                                    Email Notifications
                                </h2>
                            </div>

                            <div className="pl-4 pr-4 pb-4 pt-1 space-y-4">
                                {isLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="w-5 h-5 animate-spin text-on-surface-variant" />
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between p-3 rounded-md border border-outline-variant/20 bg-white">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <Bell className="w-4 h-4 text-on-surface-variant flex-shrink-0" />
                                                <span className="text-sm font-medium text-on-surface">
                                                    Email me when followed players play
                                                </span>
                                            </div>
                                            <Toggle
                                                checked={followedPlayerEnabled}
                                                onChange={() => setFollowedPlayerEnabled((v) => !v)}
                                            />
                                        </div>

                                        <div className="flex items-center justify-between p-3 rounded-md border border-outline-variant/20 bg-white">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <FileText className="w-4 h-4 text-on-surface-variant flex-shrink-0" />
                                                <span className="text-sm font-medium text-on-surface">
                                                    Email me game summaries
                                                </span>
                                            </div>
                                            <Toggle
                                                checked={summaryEnabled}
                                                onChange={() => setSummaryEnabled((v) => !v)}
                                            />
                                        </div>

                                        <div
                                            className={`flex items-center gap-3 p-3 rounded-md border bg-white transition-opacity ${
                                                anyEnabled
                                                    ? "border-outline-variant/20 opacity-100"
                                                    : "border-outline-variant/10 opacity-50 pointer-events-none"
                                            } ${
                                                hasValidationError || !isValidEmail
                                                    ? "border-red-400"
                                                    : email && isDirty
                                                    ? "border-blue-400"
                                                    : ""
                                            }`}
                                        >
                                            <Mail className="w-4 h-4 text-on-surface-variant flex-shrink-0" />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Notification email address"
                                                disabled={!anyEnabled}
                                                className="flex-1 bg-white text-sm text-on-surface placeholder-on-surface-variant focus:outline-none transition-colors"
                                            />
                                        </div>

                                        {hasValidationError && (
                                            <p className="text-xs text-red-500 pl-1">
                                                An email address is required when notifications are enabled.
                                            </p>
                                        )}
                                        {!hasValidationError && !isValidEmail && (
                                            <p className="text-xs text-red-500 pl-1">
                                                Please enter a valid email address.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
